import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
	GetRelatedTranslationRulesResponse,
	SystemCodesCreateResponse,
	SystemCodesDeleteRequest,
	SystemCodesDeleteResponse,
	SystemCodesReadRequest,
	SystemCodesReadResponse,
	SystemCodesUpdateResponse
} from '@helpers/types/hlr';
import { 
	AbstractCRUDService, 
	AlarisApiService, 
	ExtendableRefBookService
} from '@campaign-portal/components-library';
import { SystemCode } from '@helpers/types/models';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';

@Injectable({
	providedIn: 'root'
})
export class SystemCodesService extends ExtendableRefBookService<SystemCode> implements AbstractCRUDService {


	readonly entity = '';
	readonly title = '';
	override readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService
	) {
		super();
	}

	override process(resp: ReadResponse<SystemCode<number | null>[]>): ReadResponse<SystemCode<number | null>[]> {
		this.list$.next(resp.Data);
		this.map$.next(this.list.reduce((result, item) => {
			result.set(item.id, item);
			return result;
		}, new Map()));
		return resp;
	}

	read(params?: ReadRequest): Observable<ReadResponse<SystemCode[]>> {
		let parsedParams: SystemCodesReadRequest = {
			active: 'id',
			direction: 'asc',
		}
		if (params) {
			parsedParams = this.prepareParams(params);
		}
		return this.api.loader<SystemCodesReadResponse>('SystemCodes.Read', parsedParams, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	update(params: SystemCode): Observable<SystemCodesUpdateResponse | SystemCodesCreateResponse> {
		const method = params.id ? 'SystemCodes.Update' : 'SystemCodes.Create';
		return this.api.loader<SystemCodesUpdateResponse 
			| SystemCodesCreateResponse>(method, params, this.loading$);
	}

	delete(params: SystemCodesDeleteRequest): Observable<SystemCodesDeleteResponse> {
		return this.api.loader<SystemCodesDeleteResponse>('SystemCodes.Delete', params, this.loading$);
	}

	getRelatedTranslationRules(id: number | null): Observable<GetRelatedTranslationRulesResponse> {
		return this.api.loader<GetRelatedTranslationRulesResponse>
		('SystemCodes.GetRelatedTranslationRules', {id: id}, this.loading$);
	}

	private prepareParams(params: RPCRequestParams): SystemCodesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'id',
			direction: 'asc',
			filter: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				})
				: []
		};
	}

	private parseFilterField(filter: Filter): { key: string, value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'Value':
				return { key: 'value', value: value as string };
			case 'Type':
				if (value === 'Any') {
					return { key: 'type', value: '' };
				} else {
					return { key: 'type', value: value as string };
				}
			default:
				return { key: variable, value: value + '' };
		}
	}

}
